import React, { useMemo } from "react";
import styled, { css } from "styled-components";

import { HexTooltip } from "../../../../hex-components";
import { HexToolTipProps } from "../../../../hex-components/HexTooltip";
import { CategoryLabel } from "../CategoryLabel";

import { AppliedLabelsOverflowCount } from "./AppliedLabelsOverflowCount.js";
import { AppliedLabelsWrapper, CollapsedAppliedLabelsWrapper } from "./styled";
import { AppliedCategoriesProps } from "./types.js";

// --- Provider ---

type ContextValue = {
  appliedCategories: AppliedCategoriesProps["appliedCategories"];
  collapsed: boolean;
  collapseAfter: number | null;
  collapsedCategories: AppliedCategoriesProps["appliedCategories"];
  condensed: boolean;
  showOverflowCount: boolean;
  minimal: boolean;
};

const Context = React.createContext<ContextValue | null>(null);

const useContext = (): NonNullable<ContextValue> => {
  const context = React.useContext(Context);
  if (context == null) {
    throw new Error("StatusLabel must be used within a StatusLabelProvider");
  }
  return context;
};

export interface AppliedCategoriesProviderProps extends Omit<
  React.ComponentProps<typeof Context.Provider>,
  "value"
> {
  appliedCategories: ContextValue["appliedCategories"];
  condensed?: boolean;
  minimal?: boolean;
  collapseAfter?: number | null;
  showOverflowCount?: boolean;
}

const Provider = React.memo(function Provider(
  props: AppliedCategoriesProviderProps,
) {
  const {
    appliedCategories,
    collapseAfter = 2,
    condensed = false,
    minimal = false,
    showOverflowCount = false,
    ...rest
  } = props;

  const collapsed =
    minimal ||
    (collapseAfter != null && appliedCategories.length > collapseAfter);

  const collapsedCategories = useMemo(
    () =>
      showOverflowCount && collapseAfter != null
        ? appliedCategories.slice(0, collapseAfter)
        : appliedCategories,
    [appliedCategories, collapseAfter, showOverflowCount],
  );

  const value = React.useMemo<ContextValue>(
    () => ({
      appliedCategories,
      collapsed,
      collapseAfter,
      collapsedCategories,
      condensed,
      showOverflowCount,
      minimal,
    }),
    [
      appliedCategories,
      collapsed,
      collapseAfter,
      collapsedCategories,
      condensed,
      showOverflowCount,
      minimal,
    ],
  );

  return <Context.Provider value={value} {...rest} />;
});

// --- Tooltip ---

export interface AppliedCategoriesTooltipProps extends Partial<HexToolTipProps> {
  contentClassName?: string;
}

const Tooltip = React.memo(function AppliedCategoriesTooltip(
  props: AppliedCategoriesTooltipProps,
) {
  const { contentClassName, ...rest } = props;

  const { appliedCategories, collapsed, condensed } = useContext();

  return (
    <HexTooltip
      {...rest}
      content={
        <AppliedLabelsWrapper className={contentClassName}>
          {appliedCategories.map((category) => (
            <CategoryLabel
              key={category.id}
              category={category}
              condensed={condensed}
            />
          ))}
        </AppliedLabelsWrapper>
      }
      disabled={!collapsed}
      placement="bottom"
    />
  );
});

// --- Labels ---

export interface AppliedCategoriesLabelsProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    Pick<AppliedCategoriesProps, "muted"> {}

const Labels = React.memo(
  React.forwardRef<HTMLDivElement, AppliedCategoriesLabelsProps>(
    (props, ref) => {
      const { className, muted = false } = props;

      const {
        appliedCategories,
        collapseAfter,
        collapsed,
        collapsedCategories,
        condensed,
        minimal,
        showOverflowCount,
      } = useContext();

      if (!collapsed) {
        return (
          <AppliedCategoriesWrapper
            ref={ref}
            $minimal={minimal}
            className={className}
          >
            {appliedCategories.map((category) => (
              <StyledCategoryLabelWithTooltip
                key={category.id}
                category={category}
                condensed={condensed}
                minimal={minimal}
                muted={muted}
              />
            ))}
          </AppliedCategoriesWrapper>
        );
      }

      return (
        <CollapsedAppliedLabelsWrapper ref={ref} className={className}>
          {collapsedCategories.map((category) => (
            <StyledCategoryLabel
              key={category.name}
              category={category}
              condensed={condensed}
              minimal={true}
            />
          ))}
          {showOverflowCount && collapseAfter != null && (
            <AppliedLabelsOverflowCount
              collapseAfter={collapseAfter}
              muted={muted}
              numberOfAppliedLabels={appliedCategories.length}
            />
          )}
        </CollapsedAppliedLabelsWrapper>
      );
    },
  ),
);

const CategoryLabelStyles = css<{ minimal?: boolean }>`
  padding: ${({ minimal }) => (minimal ? 0 : undefined)};
  width: ${({ minimal }) => (minimal ? "auto" : undefined)};
`;

const StyledCategoryLabelWithTooltip = styled(CategoryLabel)`
  ${CategoryLabelStyles};
`;

const StyledCategoryLabel = styled(CategoryLabel.Label)`
  ${CategoryLabelStyles};
`;

const AppliedCategoriesWrapper = styled(AppliedLabelsWrapper)`
  gap: 2px;
`;

// --- Composition ---

const Composition = React.memo(function Composition({
  appliedCategories,
  className,
  collapseAfter = 2,
  condensed,
  isExcludeFilter = false,
  minimal,
  muted = false,
  showOverflowCount = false,
}: AppliedCategoriesProps) {
  if (isExcludeFilter) {
    return <AppliedLabelsWrapper>None</AppliedLabelsWrapper>;
  }

  return (
    <Provider
      appliedCategories={appliedCategories}
      collapseAfter={collapseAfter}
      condensed={condensed}
      minimal={minimal}
      showOverflowCount={showOverflowCount}
    >
      <Tooltip contentClassName={className}>
        <Labels className={className} muted={muted} />
      </Tooltip>
    </Provider>
  );
});

/**
 * @example
 * <AppliedCategories.Provider appliedCategories condensed minimal>
 *   <AppliedCategories.Tooltip
 *     renderTarget={({ isOpen: _isOpen, ...tooltipTargetProps }) => (
 *       <AppliedCategories.Labels muted {...tooltipTargetProps} />
 *     )}
 *   />
 * </AppliedCategories.Provider>
 */
export const AppliedCategories = Object.assign(Composition, {
  Tooltip,
  Provider,
  Labels,
});
